/**
 * What the per-form exports send the server (2026-09-14).
 *
 * "CRF as Word", "CRF as PDF" and the two annotated downloads rebuild the
 * form on the server from the page's copy of it. Each site used to inline
 * its own five-field copy of every variable and send nothing of the form's
 * structure, so a note, a citation, a parent flag, an indent and the
 * form-level instruction line - everything the content pass writes - reached
 * the batch render (which reads the stored form) but never the per-form one:
 * two renders of one form, disagreeing. One helper now builds the payload
 * for every site. Pure, so `npm run test:web` can reach it.
 */
import { structurePayloadFields, type ClientStructureChoice } from './crfStructurePayload';
import { carriedQuestionFields } from './crfCarriedFields';

type ExportableVariable = {
  varName: string;
  questionText: string;
  shortLabel?: string;
  responseType: string;
  options?: unknown;
  unit?: string;
  guidance?: string;
  guidanceSource?: string;
  protocolRef?: string;
  isParent?: boolean;
  indent?: number;
  helpText?: string;
  required?: boolean;
  codesFrom?: string;
};

export interface ExportVariablePayload {
  varName: string;
  questionText: string;
  /** A table column's short header (§C6, 2026-09-18). Sent only when set. */
  shortLabel?: string;
  responseType: string;
  options?: unknown;
  unit?: string;
  guidance?: string;
  guidanceSource?: string;
  protocolRef?: string;
  isParent?: boolean;
  indent?: number;
  helpText?: string;
  required?: boolean;
  codesFrom?: string;
}

/** The five fields every export always sent, plus the margin content and nesting when present. */
export function exportVariablePayload(v: ExportableVariable): ExportVariablePayload {
  const out: ExportVariablePayload = {
    varName: v.varName,
    questionText: v.questionText,
    responseType: v.responseType,
    options: v.options,
    unit: v.unit,
    ...(typeof v.shortLabel === 'string' && v.shortLabel.trim() ? { shortLabel: v.shortLabel } : {}),
  };
  // Margin content and the dictionary's details: the one carried list.
  return { ...out, ...carriedQuestionFields(v) };
}

export interface ExportStructurePayload {
  structure?: {
    layout?: string;
    orientation?: string;
    instructions?: string;
    codeTables?: ClientStructureChoice['codeTables'];
  };
}

/**
 * The structural fields the renderer reads - the same ones the save payload
 * persists - and none of the provenance. Nothing in, nothing out.
 */
export function exportStructurePayload(structure: ClientStructureChoice | null | undefined): ExportStructurePayload {
  const f = structurePayloadFields(structure);
  const s: NonNullable<ExportStructurePayload['structure']> = {};
  if (f.layout) s.layout = f.layout;
  if (f.orientation) s.orientation = f.orientation;
  if (f.instructions) s.instructions = f.instructions;
  if (f.codeTables) s.codeTables = f.codeTables;
  return Object.keys(s).length > 0 ? { structure: s } : {};
}
